import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LanguageSwitcher = ({ languages, variant, showLabel = false, className = "" }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Default languages
  const defaultLangs = [
    { code: "en", label: "EN", name: "English" },
    { code: "da", label: "DA", name: "Danish" },
  ];

  // Use provided languages or default ones
  const langs = languages || defaultLangs;

  // Determine if we should use dropdown (when props are provided or variant is dropdown)
  const useDropdown = languages || variant === "dropdown";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (useDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [useDropdown]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("i18nextLng", code);
    setIsOpen(false);
  };

  const getCurrentLanguage = () => {
    return langs.find((lang) => lang.code === i18n.language) || langs[0];
  };

  // Button group variant
  if (!useDropdown) {
    return (
      <div className={`flex gap-0 border border-gray-300 rounded-md overflow-hidden ${className}`}>
        {langs.map(({ code, label }, idx) => (
          <button
            key={code}
            onClick={() => handleLanguageChange(code)}
            className={`px-3 py-1.5 text-sm font-semibold transition-all duration-200 min-w-[40px]
              ${
                i18n.language === code
                  ? "bg-secondaryColor text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }
              ${idx === 0 ? "" : "border-l border-gray-300"}
            `}
          >
            {label}
          </button>
        ))}
      </div>
    );
  }

  // Dropdown variant
  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-green-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-secondaryColor focus:border-secondaryColor transition-all duration-200"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <span className="w-6 h-4 rounded-sm flex items-center justify-center text-xs font-bold">
            {getCurrentLanguage().label}
          </span>
        </div>

        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 w-full mt-1 bg-white">
          <div className="py-1">
            {langs.map(({ code, label, name }) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code)}
                className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors duration-150
                  ${
                    i18n.language === code
                      ? "bg-secondaryColor text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }
                `}
              >
                <span
                  className={`w-6 h-4 rounded-sm flex items-center justify-center text-xs font-bold
                 
                `}
                >
                  {label}
                </span>
                {i18n.language === code && (
                  <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
